import dayjs from "dayjs";
import cls from "classnames";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DecimalHelper } from "@/helpers";
import { TransferHelper } from "@/domains/transfer";
import {
  WalletCategory,
  WalletRead,
  MyWalletService,
  WalletStatus,
} from "@/domains/wallet";
import { useMessage } from "@/contexts";
import { useAppDispatch, useAppSelector } from "@/stores";
import {
  operatorManageWalletSelects,
  operatorManageWalletThunks,
} from "./store";
// components
import { Button, Modal, Table, Tag, Tooltip } from "antd";
import { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { MoneyCollectOutlined } from "@ant-design/icons";
import { CopyIconButton } from "@/components";
import { CreateModal } from "./CreateModal";
import { UpdateModal, UpdateModalRef } from "./UpdateModal";
import { TransferModal, TransferModalRef } from "./TransferModal";
import { RentEnergyModal, RentEnergyModalRef } from "./RentEnergyModal";

export const OperatorManageWalletsPage = () => {
  const updateModalRef = useRef<UpdateModalRef>(null);

  const transferModalRef = useRef<TransferModalRef>(null);

  const rentEnergyModalRef = useRef<RentEnergyModalRef>(null);

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const loading = useAppSelector(operatorManageWalletSelects.loading);

  const wallets = useAppSelector(operatorManageWalletSelects.wallets);

  const total = useAppSelector(operatorManageWalletSelects.total);

  const pageQuery = useAppSelector(operatorManageWalletSelects.pageQuery);

  const { messageApi } = useMessage();

  useEffect(() => {
    void dispatch(operatorManageWalletThunks.list(pageQuery));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    void dispatch(
      operatorManageWalletThunks.list({
        page_index: pagination.current!,
        page_size: pagination.pageSize!,
      })
    );
  };

  const handleUpdateModalUpdate = () => {
    void dispatch(operatorManageWalletThunks.list(pageQuery));
  };

  const handleTransferModalTransfer = () => {
    void dispatch(operatorManageWalletThunks.list(pageQuery));
  };

  const handleCollect = (walletId: string) => {
    Modal.confirm({
      title: t("systems.Modal.sureToCollectWallet"),
      icon: <MoneyCollectOutlined />,
      okType: "danger",
      async onOk() {
        await MyWalletService.collect(walletId).run();
        void dispatch(operatorManageWalletThunks.list(pageQuery));
        void messageApi.success(t("systems.Modal.walletCollectionCreated"));
      },
    });
  };

  const columns: ColumnsType<WalletRead> = [
    {
      width: 100,
      className: "v:top",
      title: t("domains.Wallet.belong"),
      key: "belong",
      render(_, record) {
        return <>{record.belong.name}</>;
      },
    },
    {
      width: 280,
      className: "v:top",
      title: `${t("domains.Wallet.address")} | ${t(
        "domains.Wallet.identifier"
      )}`,
      dataIndex: "address",
      render(_, record) {
        const renderEnergyTooltip = (record: WalletRead) => {
          let text = `${t("domains.Transfer.energy")}: `;
          if (record.energy == null) {
            text += "-";
          } else {
            if (record.energy == 0) {
              text += "0";
            } else {
              text += record.energy.toFixed(2);
            }
          }
          return (
            <>
              <div className="m:8">
                <span className="mr:8">{text}</span>
                <Button
                  color="danger"
                  onClick={() =>
                    rentEnergyModalRef.current?.open(record.address ?? "")
                  }
                >
                  {t("domains.Transfer.rent")}
                </Button>
              </div>
            </>
          );
        };
        const renderAddressTag = (category: number) => {
          switch (category) {
            case WalletCategory.OperatorCollect:
              return (
                <Tooltip
                  className="r:12"
                  title={renderEnergyTooltip(record)}
                  trigger="click"
                >
                  <Tag color="red" className="cursor:pointer">
                    {t("domains.Wallet.categories.OperatorCollect")}
                  </Tag>
                </Tooltip>
              );
            case WalletCategory.OperatorReceive:
              return (
                <Tooltip
                  className="r:12"
                  title={renderEnergyTooltip(record)}
                  trigger="click"
                >
                  <Tag color="red" className="cursor:pointer">
                    {t("domains.Wallet.categories.OperatorReceive")}
                  </Tag>
                </Tooltip>
              );
            case WalletCategory.TenantCollect:
              return (
                <Tooltip
                  className="r:12"
                  title={renderEnergyTooltip(record)}
                  trigger="click"
                >
                  <Tag color="blue" className="cursor:pointer">
                    {t("domains.Wallet.categories.TenantCollect")}
                  </Tag>
                </Tooltip>
              );
            case WalletCategory.TenantReceive:
              return (
                <Tooltip
                  className="r:12"
                  title={renderEnergyTooltip(record)}
                  trigger="click"
                >
                  <Tag color="blue" className="cursor:pointer">
                    {t("domains.Wallet.categories.TenantReceive")}
                  </Tag>
                </Tooltip>
              );
            case WalletCategory.TenantBrokerCollect:
              return (
                <Tooltip
                  className="r:12"
                  title={renderEnergyTooltip(record)}
                  trigger="click"
                >
                  <Tag color="green" className="cursor:pointer">
                    {t("domains.Wallet.categories.TenantBrokerCollect")}
                  </Tag>
                </Tooltip>
              );
            case WalletCategory.TenantBrokerReceive:
              return (
                <Tooltip
                  className="r:12"
                  title={renderEnergyTooltip(record)}
                  trigger="click"
                >
                  <Tag color="green" className="cursor:pointer">
                    {t("domains.Wallet.categories.TenantBrokerReceive")}
                  </Tag>
                </Tooltip>
              );
            case WalletCategory.TenantMemberCollect:
              return (
                <Tooltip
                  className="r:12"
                  title={renderEnergyTooltip(record)}
                  trigger="click"
                >
                  <Tag color="orange" className="cursor:pointer">
                    {t("domains.Wallet.categories.TenantMemberCollect")}
                  </Tag>
                </Tooltip>
              );
            case WalletCategory.TenantMemberReceive:
              return (
                <Tooltip
                  className="r:12"
                  title={renderEnergyTooltip(record)}
                  trigger="click"
                >
                  <Tag color="orange" className="cursor:pointer">
                    {t("domains.Wallet.categories.TenantMemberReceive")}
                  </Tag>
                </Tooltip>
              );
          }
        };
        return (
          <>
            <div>
              <a
                target="_blank"
                className="mr:4"
                href={TransferHelper.getAddressLink(record.address)}
              >
                {record.address}
              </a>
              {renderAddressTag(record.category)}
              <CopyIconButton text={record.address} />
            </div>
            <div>{record.source}</div>
          </>
        );
      },
    },
    {
      width: 120,
      className: "v:top t:right",
      title: t("domains.Wallet.balance"),
      render(_, record) {
        switch (record.currency) {
          case "USDT": {
            return (
              <>
                <div>
                  <span className="inline-block w:65 t:right">
                    {DecimalHelper.fixed(record.balance1, 2)}
                  </span>
                  <span className="inline-block w:35 t:right">USDT</span>
                </div>
                <div>
                  <span className="inline-block w:65 t:right">
                    {DecimalHelper.fixed(record.balance2, 2)}
                  </span>
                  <span className="inline-block w:35 t:right">TRX</span>
                </div>
              </>
            );
          }
          default: {
            return (
              <div>
                <span className="inline-block w:80 t:right">
                  {DecimalHelper.fixed(record.balance1, 2)}
                </span>
                <span className="inline-block w:35 t:right">
                  {record.currency}
                </span>
              </div>
            );
          }
        }
      },
    },
    {
      width: 75,
      className: "v:top t:right",
      title: t("domains.Wallet.balanceMax"),
      dataIndex: ["balance_max"],
    },
    {
      width: 95,
      className: "v:top t:right",
      title: t("domains.Wallet.dailyTotalDeposit"),
      key: "daily_deposit",
      sorter: () => 1,
      render(_, { daily_deposit }) {
        return (
          <div className="t:right">
            {parseInt(daily_deposit).toLocaleString()}
          </div>
        );
      },
    },
    {
      width: 95,
      className: "v:top t:right",
      title: t("domains.Wallet.weeklyTotalDeposit"),
      key: "weekly_deposit",
      sorter: () => 1,
      render(_, { weekly_deposit }) {
        return (
          <div className="t:right">
            {parseInt(weekly_deposit).toLocaleString()}
          </div>
        );
      },
    },
    {
      width: 95,
      className: "v:top t:right",
      title: t("domains.Wallet.monthlyTotalDeposit"),
      key: "monthly_deposit",
      sorter: () => 1,
      render(_, { monthly_deposit }) {
        return (
          <div className="t:right">
            {parseInt(monthly_deposit).toLocaleString()}
          </div>
        );
      },
    },
    {
      width: 95,
      className: "v:top t:right",
      title: t("domains.Wallet.dailyDepositMax"),
      key: "daily_deposit_max",
      sorter: () => 1,
      render(_, { daily_deposit_max }) {
        return (
          <div className="t:right">
            {parseInt(daily_deposit_max).toLocaleString()}
          </div>
        );
      },
    },
    {
      width: 95,
      className: "v:top t:right",
      title: t("domains.Wallet.weeklyDepositMax"),
      key: "weekly_deposit_max",
      sorter: () => 1,
      render(_, { weekly_deposit_max }) {
        return (
          <div className="t:right">
            {parseInt(weekly_deposit_max).toLocaleString()}
          </div>
        );
      },
    },
    {
      width: 95,
      className: "v:top t:right",
      title: t("domains.Wallet.monthlyDepositMax"),
      key: "monthly_deposit_max",
      sorter: () => 1,
      render(_, { monthly_deposit_max }) {
        return (
          <div className="t:right">
            {parseInt(monthly_deposit_max).toLocaleString()}
          </div>
        );
      },
    },
    {
      title: t("domains.Wallet.note"),
      className: "v:top",
      dataIndex: "note",
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: t("domains.Wallet.createdAt"),
      key: "created_at",
      sorter: () => 1,
      render(_, { created_at }) {
        return <div>{dayjs.$ldts(created_at)}</div>;
      },
    },
    {
      width: 210,
      className: "v:top",
      title: t("domains.Wallet.status"),
      key: "status",
      render(_, record) {
        const StatusButton = () => (
          <Button danger type="primary">
            {record.status === WalletStatus.disabled
              ? t("systems.Action.enable")
              : t("systems.Action.disable")}
          </Button>
        );

        const UpdateButton = () => (
          <Button
            type="primary"
            onClick={() => updateModalRef.current?.open(record)}
          >
            {t("domains.Wallet.setting")}
          </Button>
        );

        const isCollectable =
          record.category === WalletCategory.OperatorReceive;
        const CollectButton = () =>
          record.used ? (
            <Button type="primary" disabled>
              {t("domains.Wallet.collect")}
            </Button>
          ) : (
            <Button type="primary" onClick={() => handleCollect(record.id)}>
              {t("domains.Wallet.collect")}
            </Button>
          );

        const isTransferable =
          record.category === WalletCategory.OperatorCollect;
        const TransferButton = () => (
          <Button
            type="primary"
            className="bg:gold-66"
            onClick={() => transferModalRef.current?.open(record)}
          >
            {t("domains.Wallet.transfer")}
          </Button>
        );

        return (
          <>
            <div>
              <span
                className={cls({
                  "fg:primary": record.status === WalletStatus.enabled,
                  "fg:disabled": record.status === WalletStatus.disabled,
                })}
              >
                {record.status === WalletStatus.enabled
                  ? t("systems.Status.enabled")
                  : t("systems.Status.disabled")}
              </span>
            </div>
            <div className="flex gap:8">
              <StatusButton />
              <UpdateButton />
              {isCollectable && <CollectButton />}
              {isTransferable && <TransferButton />}
            </div>
          </>
        );
      },
    },
  ];

  const getRowClassName = (record: WalletRead) => {
    if (record.status === WalletStatus.disabled) {
      return "fg:disabled";
    }
    return "";
  };

  return (
    <div className="flex flex:col gap:8">
      <div className="flex jc:end gap:8">
        <CreateModal />
      </div>

      <Table
        rowKey="id"
        tableLayout="fixed"
        loading={loading}
        columns={columns}
        dataSource={wallets}
        scroll={{ x: "1600px" }}
        pagination={{
          current: pageQuery.page_index,
          pageSize: pageQuery.page_size,
          total: total,
        }}
        onChange={handleTableChange}
        rowClassName={getRowClassName}
      ></Table>

      <UpdateModal
        ref={updateModalRef}
        onUpdate={() => handleUpdateModalUpdate()}
      />

      <TransferModal
        ref={transferModalRef}
        onTransfer={() => handleTransferModalTransfer()}
      />

      <RentEnergyModal ref={rentEnergyModalRef} />
    </div>
  );
};
